import { useRef, useState } from "react";
import { Film, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, resolveUrl } from "@/lib/api";

const CHUNK = 5 * 1024 * 1024;
const MAX = 200 * 1024 * 1024;

export const VideoUpload = ({ value, onChange }) => {
  const inputRef = useRef();
  const [progress, setProgress] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) return toast.error("Το βίντεο ξεπερνά τα 200MB");
    try {
      setProgress(0);
      const { data } = await api.post("/upload/video/init", { filename: file.name, size: file.size });
      for (let off = 0; off < file.size; off += CHUNK) {
        const fd = new FormData();
        fd.append("chunk", file.slice(off, off + CHUNK), "chunk");
        await api.post(`/upload/video/chunk?upload_id=${data.upload_id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round((Math.min(off + CHUNK, file.size) / file.size) * 95));
      }
      const res = await api.post(`/upload/video/complete?upload_id=${data.upload_id}`);
      onChange(res.data.url);
      toast.success("Το βίντεο ανέβηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία μεταφόρτωσης βίντεο");
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="biz-video-upload">
      <label className="block text-xs font-semibold mb-1 text-slate-600">Βίντεο (MP4 / MOV / WEBM, έως 200MB)</label>
      {value && progress === null && (
        <div className="relative rounded-lg overflow-hidden bg-black mb-2">
          <video src={resolveUrl(value)} controls preload="metadata" className="w-full max-h-48" data-testid="biz-video-preview" />
          <button type="button" onClick={() => onChange("")} data-testid="biz-video-remove"
            className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-white/90 text-rose-600 hover:bg-white"><Trash2 size={15} /></button>
        </div>
      )}
      {progress !== null ? (
        <div className="h-9 rounded-lg bg-muted overflow-hidden relative" data-testid="biz-video-progress">
          <div className="h-full bg-primary/30 transition-[width] duration-300" style={{ width: `${progress}%` }} />
          <span className="absolute inset-0 flex items-center justify-center gap-2 text-xs font-semibold"><Loader2 size={14} className="animate-spin" /> Ανέβασμα... {progress}%</span>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} data-testid="biz-video-upload-btn"
          className="w-full h-9 rounded-lg border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-600 hover:border-primary hover:text-primary inline-flex items-center justify-center gap-2 transition-colors">
          {value ? <><Upload size={15} /> Αντικατάσταση βίντεο</> : <><Film size={15} /> Ανέβασμα βίντεο</>}
        </button>
      )}
      <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid="biz-video-file-input" />
    </div>
  );
};
